// ═══════════════════════════════════════════════════════════════════════════
// PIED DE PAGE DES PAGES PUBLIQUES CP — les chemins qui manquaient
// ═══════════════════════════════════════════════════════════════════════════
//
// ⚠️ MESURE DU 26/08/2026, SUR LE HTML SERVI. Une page de barème n'émettait
// que QUATRE liens internes : /baremes, /contact, son jumeau calculateur et sa
// jumelle linguistique. Rien d'autre. Pas d'accueil, pas de tarifs, pas de
// blog, pas de page « à propos ».
//
// Ce sont pourtant les pages les MIEUX classées du site (l'en-tête de
// app/baremes/_render.js note, daté du 22/08, une 1re position sur « barème
// CP 200 »). Trois conséquences, toutes mesurables :
//
//   1. Aucune autorité ne remonte vers les pages commerciales. Les 474 pages
//      qui reçoivent les visites ne renforcent ni /pricing ni /blog.
//   2. Aucun chemin de confiance. Sur un sujet où l'on donne des chiffres de
//      droit social, /about — qui porte la BCE et l'adresse — est un signal
//      E-E-A-T, et il n'était atteignable depuis aucune de ces pages.
//   3. Aucun chemin pour le visiteur. Quelqu'un qui arrive sur « barème CP
//      124 » n'avait qu'une sortie : /contact.
//
// Deux pages étaient carrément ORPHELINES — zéro lien entrant sur tout le
// site : /about et /optimisation-cout-employeur. La seconde était en plus
// absente du sitemap tout en étant `index, follow` : une page commerciale
// entièrement dans le noir.
//
// ⚠️ ON NE LIE QUE VERS CE QUI EXISTE, ET DANS LA BONNE LANGUE. Le
// néerlandais n'a pas d'équivalent pour /pricing, /blog ni
// /optimisation-cout-employeur : son pied de page est donc plus court, il ne
// pointe pas vers des pages françaises pour faire nombre. Un lien qui déçoit
// vaut moins que pas de lien. La liste est verrouillée par un test qui vérifie
// que chaque cible est bien déclarée au sitemap.

/** Les liens du pied de page, par langue. `libelle` sert d'ancre. */
export // ⚠️ LES DEUX LANGUES DIVERGEAIENT, DANS LES DEUX SENS — mesuré le 29/08/2026.
// Le pied de page FR portait 7 liens, le NL seulement 5, et ce n'était pas une
// traduction incomplète : chacun avait ce que l'autre n'avait pas. Le FR citait
// /blog et /optimisation-cout-employeur, absents du NL ; le NL citait
// /sociaal-secretariaat et /tools-integreren dont les jumelles françaises
// manquaient. Et surtout : NI l'un NI l'autre ne citait les hubs de barèmes ou
// de calculateurs, alors que 79 % du trafic Google du site atterrit dessus.
//
// 💰 LE CHEMIN VERS LA VENTE MANQUAIT AU NÉERLANDOPHONE, et il est rétabli —
// mais pas comme je l'avais d'abord cru. Première conclusion, FAUSSE :
// « /pricing n'est pas traduit », déduite en comptant les `lang === 'nl'`
// écrits dans le CORPS de la page (il y en a 3). Le vrai contenu vit dans son
// dictionnaire `T`, que ce motif ne voyait pas : /pricing porte 76 lignes de
// néerlandais, autant que de français. Des octets reçus plus un motif suspect
// donnent une conclusion fausse (R2 ter).
// Le défaut réel était ailleurs : la page démarrait toujours en français. Elle
// honore désormais `?lang=`, d'où le lien `/pricing?lang=nl` ci-dessous.
//
// ⚠️ `/about` A MAINTENANT SA JUMELLE : /over-ons, créée le 29/08/2026. La
// note précédente disait « français seulement, 65 lignes, pas un mot de
// néerlandais » — c'était vrai ce jour-là, ça ne l'est plus. Le pied de page
// NL pointe vers /over-ons ; il continue de ne PAS pointer vers /about, et
// __tests__/pied-de-page-cp.test.js a toujours raison de l'y interdire —
// c'est l'URL française qui est interdite, pas le sujet.
// C'est la page de CONFIANCE (BCE, adresse, statut RSZ) : la priver du
// néerlandophone, c'est lui demander de signer sans preuve.
//
// Règle tenue ici : toute entrée d'une langue a sa jumelle dans l'autre, sauf
// celles listées avec leur raison dans le verrou de symétrie.
// Verrou : __tests__/liens-pied-page-symetrie.test.js
const LIENS_PIED_PAGE = {
  fr: [
    { href: '/', libelle: 'Accueil' },
    { href: '/pricing', libelle: 'Tarifs' },
    { href: '/secteurs', libelle: 'Secteurs' },
    { href: '/baremes', libelle: 'Barèmes par CP' },
    { href: '/calculateur', libelle: 'Calculateurs' },
    { href: '/secretariat-social', libelle: 'Secrétariat social' },
    { href: '/outils-a-integrer', libelle: 'Outils à intégrer' },
    { href: '/optimisation-cout-employeur', libelle: 'Optimiser le coût employeur' },
    { href: '/blog', libelle: 'Blog' },
    { href: '/about', libelle: 'À propos' },
    { href: '/contact', libelle: 'Contact' },
  ],
  nl: [
    { href: '/', libelle: 'Home' },
    { href: '/pricing?lang=nl', libelle: 'Tarieven' },
    { href: '/sectoren', libelle: 'Sectoren' },
    { href: '/barema', libelle: 'Barema’s per PC' },
    { href: '/berekening', libelle: 'Berekeningen' },
    { href: '/gids', libelle: 'Gidsen' },
    { href: '/sociaal-secretariaat', libelle: 'Sociaal secretariaat' },
    { href: '/tools-integreren', libelle: 'Tools integreren' },
    { href: '/over-ons', libelle: 'Over ons' },
    { href: '/contact', libelle: 'Contact' },
  ],
};

/** Les liens de la langue demandée ; français par défaut. */
export function liensPiedPage(lang) {
  return LIENS_PIED_PAGE[lang] || LIENS_PIED_PAGE.fr;
}

/** Toutes les cibles, toutes langues confondues — sert au verrou. */
export function ciblesPiedPage() {
  return [...new Set(Object.values(LIENS_PIED_PAGE).flat().map((l) => l.href))];
}
